import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Popover, Spin } from 'antd';
import { CheckOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';

import { TPO_OPTIMIZE_CONSTRAINT_SELECTOR, tpoOptionTestId } from '../../../../config/testIds';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';

const OVERLAY_INNER_STYLE = { padding: 0, borderRadius: 8, overflow: 'hidden' } as const;

interface ConstraintItem {
    name: string;
    label: string;
}

interface Props {
    items: ConstraintItem[];
    selectedNames: string[];
    onApply: (selectedNames: string[]) => void;
    disabled?: boolean;
    loading?: boolean;
}

const ConstraintSelector: React.FC<Props> = ({ items, selectedNames, onApply, disabled = false, loading = false }) => {
    const { labels } = useTpoGenConfig();
    const selectorLabels = labels.optimizePlan.selector;

    const [open, setOpen] = useState(false);
    const [tempSelected, setTempSelected] = useState<Set<string>>(() => new Set(selectedNames));
    const [search, setSearch] = useState('');

    // Mirrors `tempSelected` so event handlers can read the latest value without
    // being re-created on every toggle, and without side effects in state updaters.
    const tempSelectedRef = useRef(tempSelected);
    const selectedNamesRef = useRef(selectedNames);
    selectedNamesRef.current = selectedNames;

    const isDisabled = disabled || loading;

    const filtered = useMemo(() => {
        if (!search) return items;
        const needle = search.toLowerCase();
        return items.filter((i) => i.label.toLowerCase().includes(needle));
    }, [items, search]);

    const allFilteredSelected = filtered.length > 0 && filtered.every((item) => tempSelected.has(item.name));

    const commitSelection = useCallback((next: Set<string>) => {
        tempSelectedRef.current = next;
        setTempSelected(next);
    }, []);

    const handleOpen = useCallback(() => {
        commitSelection(new Set(selectedNamesRef.current));
        setSearch('');
        setOpen(true);
    }, [commitSelection]);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSearch('');
    }, []);

    const toggleItem = useCallback(
        (name: string) => {
            const next = new Set(tempSelectedRef.current);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            commitSelection(next);
        },
        [commitSelection],
    );

    // Operates only on the currently visible (search-filtered) rows so selections
    // made outside the active search are preserved.
    const toggleSelectAll = useCallback(() => {
        const prev = tempSelectedRef.current;
        const visibleNames = filtered.map((i) => i.name);
        const allVisibleSelected = visibleNames.length > 0 && visibleNames.every((name) => prev.has(name));

        const next = new Set(prev);
        for (const name of visibleNames) {
            if (allVisibleSelected) next.delete(name);
            else next.add(name);
        }
        commitSelection(next);
    }, [filtered, commitSelection]);

    const handleApply = useCallback(() => {
        setOpen(false);
        setSearch('');
        onApply(Array.from(tempSelectedRef.current));
    }, [onApply]);

    const handleCancel = useCallback(() => {
        commitSelection(new Set(selectedNamesRef.current));
        setOpen(false);
        setSearch('');
    }, [commitSelection]);

    const handleOpenChange = useCallback(
        (visible: boolean) => {
            // Antd only reports outside-click dismissals here; opening is driven by the trigger.
            if (!visible) handleCancel();
        },
        [handleCancel],
    );

    const triggerButton = (
        <button
            type="button"
            data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_trigger`}
            data-state-disabled={isDisabled ? 'true' : undefined}
            data-state-expanded={open ? 'true' : undefined}
            disabled={isDisabled}
            onClick={open ? handleClose : handleOpen}
            className={`flex items-center gap-1.5 text-sm transition-colors ${
                isDisabled
                    ? 'text-[rgba(0,0,0,0.25)] cursor-not-allowed'
                    : 'text-[rgba(0,0,0,0.65)] hover:text-[#44338a] cursor-pointer'
            }`}
        >
            {loading ? <Spin size="small" /> : <PlusCircleOutlined />}
            <span>{selectorLabels.buttonLabel}</span>
        </button>
    );

    if (isDisabled) return triggerButton;

    const dropdownContent = (
        <div className="w-[300px]" data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_panel`}>
            <div className="px-1 pb-1 flex justify-end">
                <button
                    type="button"
                    data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_select_all`}
                    data-state-all-selected={allFilteredSelected ? 'true' : undefined}
                    onClick={toggleSelectAll}
                    className="text-xs text-[#44338a] hover:underline leading-none"
                >
                    {allFilteredSelected ? selectorLabels.unselectAll : selectorLabels.selectAll}
                </button>
            </div>

            <div className="relative pt-1 pb-2">
                <SearchOutlined className="absolute left-[10px] top-[40%] -translate-y-[30%] text-[#bfbfbf] pointer-events-none" />
                <input
                    type="text"
                    data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_search_input`}
                    placeholder={selectorLabels.searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                    className="w-full pl-7 pr-3 py-1.5 text-sm border  rounded bg-[#0000000a] focus:outline-none focus:border-[#44338a]"
                />
            </div>

            <div className="max-h-48 overflow-y-auto px-0">
                {filtered.length > 0 ? (
                    filtered.map((item) => {
                        const selected = tempSelected.has(item.name);
                        return (
                            <div
                                key={item.name}
                                data-testid={tpoOptionTestId(TPO_OPTIMIZE_CONSTRAINT_SELECTOR, item.name)}
                                data-state-selected={selected ? 'true' : undefined}
                                onClick={() => toggleItem(item.name)}
                                className={`flex items-center justify-between px-3 py-1.5 rounded mb-0.5 cursor-pointer text-sm select-none transition-colors ${
                                    selected
                                        ? 'bg-[#f0eeff] text-[rgba(0,0,0,0.88)]'
                                        : 'hover:bg-[#f5f5f5] text-[rgba(0,0,0,0.88)]'
                                }`}
                            >
                                <span>{item.label}</span>
                                {selected ? <CheckOutlined className="!text-[#44338a] text-lg" /> : null}
                            </div>
                        );
                    })
                ) : (
                    <div
                        className="py-4 text-center text-sm text-[rgba(0,0,0,0.25)]"
                        data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_empty`}
                    >
                        {selectorLabels.noResults}
                    </div>
                )}
            </div>

            <div className="border-t border-[#f0f0f0] mt-1 pt-2 flex justify-end gap-2">
                <button
                    type="button"
                    data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_cancel_btn`}
                    onClick={handleCancel}
                    className="px-3 py-1 text-sm border border-[#d9d9d9] rounded hover:border-[#44338a] hover:text-[#44338a] transition-colors"
                >
                    {selectorLabels.cancel}
                </button>
                <button
                    type="button"
                    data-testid={`${TPO_OPTIMIZE_CONSTRAINT_SELECTOR}_apply_btn`}
                    onClick={handleApply}
                    className="px-3 py-1 text-sm bg-[#44338a] text-white rounded hover:bg-[#3a2d7d] transition-colors"
                >
                    {selectorLabels.apply}
                </button>
            </div>
        </div>
    );

    return (
        <Popover
            content={dropdownContent}
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            placement="bottomRight"
            showArrow={false}
            overlayInnerStyle={OVERLAY_INNER_STYLE}
        >
            {triggerButton}
        </Popover>
    );
};

ConstraintSelector.displayName = 'ConstraintSelector';
export default React.memo(ConstraintSelector);
